import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSnackbar } from "notistack";
import { addCardPaymentMethod } from "../../../endpoints/stripe.service";
import { useForm } from "react-hook-form";
import CustomInput from "../../CustomInput/CustomInput";
import { useState } from "react";
import LoadingAnimation from "../../../assets/svg/LoadingAnimation";

const CardForm = ({ sendCancel, cardAddedSuccessfully, onCardSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { enqueueSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleAddCard = async (data) => {
    const payloadData = data;
    payloadData.country = "GB";
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardNumberElement);
    setIsLoading(true);
    const { error, token } = await stripe.createToken(cardElement, payloadData);
    if (error) {
      setIsLoading(false);
      enqueueSnackbar(error?.message, { variant: "error" });
      console.log("error adding card :=> ", error);
    } else {
      addCardPaymentMethod(token.id)
        .then((response) => {
          if (response.success) {
            enqueueSnackbar("Payment method added successfully", {
              variant: "success",
            });
            // First notify success, then update the list
            if (typeof cardAddedSuccessfully === "function") {
              cardAddedSuccessfully();
            }

            // Use the new onCardSaved callback to switch tabs and refresh
            if (typeof onCardSaved === "function") {
              onCardSaved();
            }
          }
        })
        .catch((error) => enqueueSnackbar(error, { variant: "error" }))
        .finally(() => setIsLoading(false));
    }
  };

  const handleCancel = () => {
    sendCancel(true);
  };
  return (
    <form onSubmit={handleSubmit(handleAddCard)} className="">
      {/* <CardElement /> */}
      <h1 className="mb-4 text-lg md:text-xl font-semibold text-center">
        Add your Credit/Debit card
      </h1>
      <div className="border border-gray-200 px-4 sm:px-8 md:px-14 pb-6 pt-4">
        <div>
          <p className="text-xs md:text-sm text-gray-500 font-normal mb-2">
            Enter your card's registered address
          </p>
        </div>
        <div className="flex flex-col gap-2 md:gap-3 max-w-full">
          <div className="flex flex-col gap-2 md:gap-3">
            <CustomInput
              name="address_line1"
              placeholder="Street address"
              rules={{
                required: "This field is required.",
              }}
              textStyle="text-sm md:text-base"
              control={control}
              errors={errors}
            />
            <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full">
              <div className="w-full sm:w-1/2 pr-0 sm:pr-1">
                <CustomInput
                  name="address_city"
                  placeholder="City"
                  rules={{
                    required: "This field is required.",
                  }}
                  textStyle="text-sm md:text-base"
                  control={control}
                  errors={errors}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-1/2 pl-0 sm:pl-1">
                <CustomInput
                  name="address_state"
                  placeholder="State/Province"
                  rules={{
                    required: "This field is required.",
                  }}
                  textStyle="text-sm md:text-base"
                  control={control}
                  errors={errors}
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full">
              <div className="w-full sm:w-1/2 pr-0 sm:pr-1">
                <CustomInput
                  name="address_country"
                  placeholder="Country"
                  rules={{
                    required: "This field is required.",
                  }}
                  textStyle="text-sm md:text-base"
                  control={control}
                  errors={errors}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-1/2 pl-0 sm:pl-1">
                <CustomInput
                  name="address_zip"
                  placeholder="Postal Code"
                  rules={{
                    required: "This field is required.",
                  }}
                  textStyle="text-sm md:text-base"
                  control={control}
                  errors={errors}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <div className="relative rounded-lg px-3 md:px-4 py-3 md:py-4 border w-full">
            <CardNumberElement id="card-number" />
          </div>
          <div>
            <CustomInput
              name="name"
              placeholder="Name on card"
              rules={{
                required: "This field is required.",
              }}
              textStyle="text-sm md:text-base"
              control={control}
              errors={errors}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-3 w-full">
            <div className="w-full sm:w-1/2 pr-0 sm:pr-1">
              <div className="rounded-lg px-3 md:px-4 py-3 md:py-4 border w-full mb-2 md:mb-3">
                <CardExpiryElement id="card-expiry" />
              </div>
            </div>
            <div className="w-full sm:w-1/2 pl-0 sm:pl-1">
              <div className="rounded-lg px-3 md:px-4 py-3 md:py-4 border w-full mb-2 md:mb-3">
                <CardCvcElement id="card-cvc" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-6 md:mt-8 px-4 sm:px-8 md:px-14">
        <button
          className="rounded-lg border border-black font-medium text-sm md:text-base py-2 mr-2 md:mr-3 flex-1 text-center whitespace-nowrap"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center justify-center rounded-lg border bg-primary py-2 text-white text-sm md:text-base font-semibold flex-1 text-center whitespace-nowrap"
        >
          {isLoading ? (
            <LoadingAnimation className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          ) : null}
          Add Card
        </button>
      </div>
    </form>
  );
};

export default CardForm;
